import {
    html,
    LitElement,
    TemplateResult,
    CSSResult,
    css
} from 'lit';
import { customElement, state, property} from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map';
import { Site } from 'src/objectModelV2/site';
import { AirCurtain } from 'src/objectModelV2/airCurtain';
import { EVENTS, loadStyle } from '../api/ui';
import { store } from '../redux/store';
import { ObjectManager } from 'src/objectModelV2/objectManager';

@customElement('berner-sites')
export class BernerSites extends LitElement {
    private _sites: Site[] = [];
    private _airCurtainsBySite: Map<string, AirCurtain[]> = new Map();
    private _objectManager: ObjectManager;

    @property({ attribute: 'selected-site' })
    private _selectedSite = '';
    @state()
    get selectedSite(): string {
        return this._selectedSite.toString();
    }

    set selectedSite(value: string) {
        this._selectedSite = value;
    }

    @state()
    private _initialized = false;

    constructor() {
        super();
        this.init();
    }

    // class css-loading is removed as soon as the external css is loaded
    public static get styles(): CSSResult {
        return css`
            .css-loading {
                display: none;
            }
        `;
    }

    init = async (): Promise<void> => {
        const { objectManager } = store.getState();
        this._objectManager = objectManager;
        const siteGroup = this._objectManager.getSiteGroup();
        this.selectedSite = '';

        for (const siteId of siteGroup.sites) {
            const site = this._objectManager.getSite(siteId);
            this._sites.push(site);
        }

        this.loadSiteResources();
        this._initialized = true;
    }

    loadSiteResources = async (): Promise<void> => {
        for (const site of this._sites) {
            let siteCurtains: AirCurtain[] = [];
            for (const curtainGroupId of site.airCurtainGroups) {
                const airCurtainGroup = this._objectManager.getAirCurtainGroup(
                    curtainGroupId
                );
                const groupAirCurtains = await this._objectManager.getAllAirCurtainsFromAirCurtainGroup(
                    airCurtainGroup
                );
                siteCurtains = siteCurtains.concat(groupAirCurtains);
            }
            this._airCurtainsBySite.set(site.siteID, siteCurtains);
        }

        this.requestUpdate();
    }

    firstUpdated = async (): Promise<void> => {
        await loadStyle('./css/berner-sites.css', this.shadowRoot);
    };

    updated(): void {
        this.shadowRoot.getElementById(this._selectedSite)?.scrollIntoView({behavior: "smooth", block: "end", inline: "nearest"});
    }

    renderNoSites(): TemplateResult {
        return html`
            <div class="col-12">
                <div class="pt-5">
                    <p class="mx-auto mb-2 label">There are no sites yet.</p>
                </div>
            </div>
        `;
    }

    renderSites(): TemplateResult {
        if (!this._sites.length) {
            return html`${this.renderNoSites()}`;
        }

        const onClickSite = (siteId: string, e: Event) => {
            if (siteId === this.selectedSite) {
                this.selectedSite = '';
            } else {
                this.selectedSite = siteId;
            }

            const event = new CustomEvent(EVENTS.SITE_SELECTED, {
                detail: { site: this._selectedSite },
            });
            e.stopPropagation();
            window.dispatchEvent(event);
        };

        return html`
            <ul class="sites-list px-0 pb-2 content">
                ${repeat(this._sites, (site: Site) => {
                    const siteName = site.name;
                    const numCurtains = this._airCurtainsBySite.get(site.siteID)
                        ?.length;

                    return html`
                        <div class="col-12 p-0 m-0">
                            <section
                                class="${classMap({
                                    row: true,
                                    selected: this.selectedSite === site.siteID,
                                    'py-2': true,
                                    'mx-0': true,
                                })}"
                                id=${site.siteID}
                                @click=${(e: Event) => onClickSite(site.siteID, e)}>
                                <div class="site-name col-6">
                                    <p>${siteName}</p>
                                </div>
                                <div class="col-6 row pr-0 air-curtains-container">
                                    <span class="float-right mr-1">
                                        <img
                                            src="./assets/reports_icon_air_curtains_grey.svg"
                                            alt="air curtains"
                                        />
                                    </span>

                                    <span class="float-right">
                                        ${numCurtains}
                                    </span>
                                </div>
                            </section>
                        </div>
                    `;
                })}
            </ul>
        `;
    }

    render(): TemplateResult {
        return html`
        <link id="css-target">
        <div class="css-loading ml-n1" id="root">
            <div class="col-lg-12 px-2 mx-auto">
                ${this._initialized ? this.renderSites() : html``}
            </div>
        </div>`;
    }
}
